import {
    Avatar,
    Box,
    Dialog,
    DialogContent,
    Typography,
    IconButton,
    Radio,
    Button,
    DialogTitle,
    Stack,
    useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import files from 'src/utils/constants';
import { useSelector } from 'react-redux';

export const SelectUser = ({ linkedUserList, open, onClose, selectedUser, setSelectedUser }) => {
    const { t } = useTranslation('dashboard');
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const user = useSelector((state) => state.auth.user);

    const handleRadioChange = (item) => {
        // Add null checks
        if (!item?.userId) {
            console.warn('Invalid user item:', item);
            return;
        }

        const data = {
            _id: item.userId._id,
            fullName: item.userId.fullName,
            userName: item.userId.userName,
            profilePic: item.userId.profilePic
        };
        setSelectedUser(data);
        onClose();
    };

    const handleMySelf = () => {
        const mySelf = {
            _id: user?.userId,
            fullName: user?.fullName,
            userName: user?.userName,
            profilePic: user?.profilePic
        };
        setSelectedUser(mySelf);
        onClose();
    };

    // Filter out invalid items
    const validLinkedUsers = linkedUserList?.filter(item => 
        item?.userId && 
        item.userId.userName && 
        item.userId.fullName
    ) || [];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={fullScreen}
            maxWidth="xs"
            PaperProps={{
                sx: {
                    borderRadius: { xs: 0, sm: 4 },
                    boxShadow: 12,
                    width: { xs: '100%', sm: 360 },
                    mx: { xs: 0, sm: "auto" },
                    background: "#fafcff",
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pb: 0,
                pt: 2,
                px: 3,
            }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 22 }}>
                    {t('selectUserTitle')}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        color: theme.palette.grey[500],
                        ml: 1,
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent
                sx={{
                    py: 2,
                    px: { xs: 2, sm: 3 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    background: "#fafcff",
                    gap: 2,
                    minWidth: { xs: "100vw", sm: 340 },
                }}
            >
                <Button
                    variant="contained"
                    fullWidth
                    onClick={handleMySelf}
                    sx={{
                        mb: 2,
                        fontWeight: 700,
                        borderRadius: 2,
                        textTransform: 'none',
                        background: theme.palette.primary.main,
                        boxShadow: 'none',
                        fontSize: 17,
                        ':hover': { background: theme.palette.primary.dark }
                    }}
                >
                    {t('mySelf')}
                </Button>

                <Stack spacing={2} width="100%">
                    {validLinkedUsers.map((item, idx) => (
                        <Box
                            key={item.userId._id || idx}
                            display="flex"
                            alignItems="center"
                            sx={{
                                p: 1.5,
                                borderRadius: 3,
                                boxShadow: selectedUser?.userName === item?.userId?.userName ? "0 0 0 2px #6200ea" : "0 1px 2px 0 rgba(60,60,60,0.05)",
                                background: selectedUser?.userName === item?.userId?.userName ? "#f5f3fe" : "#fff",
                                border: selectedUser?.userName === item?.userId?.userName ? "1.5px solid #7c3aed" : "1px solid #e0e0e0",
                                transition: "all .15s",
                                cursor: "pointer",
                            }}
                            onClick={() => handleRadioChange(item)}
                        >
                            <Radio
                                checked={selectedUser?.userName === item?.userId?.userName}
                                onChange={() => handleRadioChange(item)}
                                value={item?.userId?.userName}
                                color="primary"
                                sx={{ mr: 1 }}
                            />
                            <Avatar
                                src={files(item.userId.profilePic, "image")}
                                sx={{
                                    width: 46,
                                    height: 46,
                                    mr: 2,
                                    bgcolor: "#ece9f5",
                                    fontWeight: 700,
                                    fontSize: 21,
                                }}
                                alt={item.userId.fullName || t('userNameFallbackCooper')}
                            >
                                {item.userId.fullName?.[0] || '?'}
                            </Avatar>
                            <Box flex={1}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: 18,
                                        color: "#211a34",
                                        lineHeight: 1.17,
                                    }}
                                    noWrap
                                >
                                    {item?.userId?.fullName}
                                </Typography>
                                <Typography
                                    sx={{
                                        color: "#7c3aed",
                                        fontSize: 15,
                                        mt: 0.2,
                                        opacity: 0.85,
                                    }}
                                    noWrap
                                >
                                    @{item.userId.userName}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Stack>

                {validLinkedUsers.length === 0 && linkedUserList?.length > 0 && (
                    <Typography 
                        sx={{ 
                            color: theme.palette.text.secondary,
                            textAlign: 'center',
                            mt: 2 
                        }}
                    >
                        {t('noValidUsersFound')}
                    </Typography>
                )}
            </DialogContent>
        </Dialog>
    );
};